import { useTolgee, useTranslate } from '@tolgee/react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import './AnalyticsSalesTable.scss';

interface SalesRecord {
  fridgeCompanyId: number;
  fridgeCompanyName: string;
  year: number;
  month: number;
  main: number;
  deserts: number;
  drinks: number;
  others: number;
  total: number;
  comboDiscount?: number;
}

interface GroupedRecord extends Omit<SalesRecord, 'year' | 'month'> {
  formattedMonth: string;
  key: string;
}

interface AnalyticsSalesTableProps {
  table1: SalesRecord[];
  table2: SalesRecord[];
  table1Loading: boolean;
  table2Loading: boolean;
}

// Utility to format data and calculate grand totals
const formatData = (data: SalesRecord[], language: string): GroupedRecord[] => {
  return data.map(item => {
    const {
      fridgeCompanyId,
      fridgeCompanyName,
      year,
      month,
      main,
      deserts,
      drinks,
      others,
      total,
      comboDiscount,
    } = item;
    const formattedMonth = new Date(year, month - 1).toLocaleString(language, {
      month: 'long',
      year: 'numeric',
    });

    return {
      key: `${fridgeCompanyId}-${month}-${year}`, // Add a unique key for each record
      fridgeCompanyId,
      fridgeCompanyName,
      formattedMonth,
      main,
      deserts,
      drinks,
      others,
      total,
      comboDiscount,
    };
  });
};

const formatNumber = (num: number) => {
  if (Number.isInteger(num)) return num; // No decimal points needed for integers
  return Number(num.toFixed(2)); // Rounds to 2 decimals if more than 2 are present
};

const calculateGrandTotal = (data: GroupedRecord[]) => {
  return data.reduce(
    (acc, record) => ({
      main: acc.main + record.main,
      drinks: acc.drinks + record.drinks,
      deserts: acc.deserts + record.deserts,
      others: acc.others + record.others,
      total: acc.total + record.total,
      comboDiscount: acc.comboDiscount + (record.comboDiscount || 0),
    }),
    { main: 0, drinks: 0, deserts: 0, others: 0, total: 0, comboDiscount: 0 },
  );
};

// Generic Sales Table Component
interface SalesTableProps {
  data: GroupedRecord[];
  displayAsPercentage?: boolean;
  displayValueInCHF?: boolean;
  showComboDiscount?: boolean;
  tableClass?: string;
  loading: boolean;
  columnDecimalPlaceValue?: number;
}

const SalesTable: React.FC<SalesTableProps> = ({
  data,
  displayAsPercentage = false,
  displayValueInCHF = false,
  showComboDiscount = false,
  tableClass,
  loading = false,
  columnDecimalPlaceValue = 0,
}) => {
  const { t } = useTranslate();

  const columns: ColumnsType<GroupedRecord> = [
    {
      title: <span className='mainTitleTable'>{t('Fridge')}</span>,
      dataIndex: 'fridgeCompanyName',
      key: 'fridgeCompanyName',
      render: text => text,
    },
    {
      title: displayValueInCHF ? (
        <span className='mainTitleTable'>{t('Value In CHF')}</span>
      ) : displayAsPercentage ? (
        <span className='mainTitleTable'>{t('Unites') + ' %'}</span>
      ) : (
        <span className='mainTitleTable'>{t('Unites')}</span>
      ),
      dataIndex: 'formattedMonth',
      key: 'formattedMonth',
      render: text => text,
    },
    {
      title: t('Meals'),
      dataIndex: 'main',
      key: 'main',
      render: (_, record) =>
        displayAsPercentage ? (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber((record.main / record.total) * 100).toFixed(
              columnDecimalPlaceValue,
            )}
            %
          </span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber(record.main).toFixed(columnDecimalPlaceValue)}
          </span>
        ),
    },
    {
      title: t('Drinks'),
      dataIndex: 'drinks',
      key: 'drinks',
      render: (_, record) =>
        displayAsPercentage ? (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber((record.drinks / record.total) * 100).toFixed(
              columnDecimalPlaceValue,
            )}
            %
          </span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber(record.drinks).toFixed(columnDecimalPlaceValue)}
          </span>
        ),
    },
    {
      title: 'Desserts',
      dataIndex: 'deserts',
      key: 'deserts',
      render: (_, record) =>
        displayAsPercentage ? (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber((record.deserts / record.total) * 100).toFixed(
              columnDecimalPlaceValue,
            )}
            %
          </span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber(record.deserts).toFixed(columnDecimalPlaceValue)}
          </span>
        ),
    },
    {
      title: t('Autres'),
      dataIndex: 'others',
      key: 'others',
      render: (_, record) =>
        displayAsPercentage ? (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber((record.others / record.total) * 100).toFixed(
              columnDecimalPlaceValue,
            )}
            %
          </span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber(record.others).toFixed(columnDecimalPlaceValue)}
          </span>
        ),
    },

    ...(showComboDiscount
      ? [
          {
            title: t('Combo Discount'),
            dataIndex: 'comboDiscount',
            key: 'comboDiscount',

            render: (value: any) =>
              value !== undefined ? (
                <span style={{ fontStyle: 'italic' }}>
                  {formatNumber(value).toFixed(columnDecimalPlaceValue)}
                </span>
              ) : (
                <span style={{ fontStyle: 'italic' }}>N/A</span>
              ),
          },
        ]
      : []),
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (_, record) =>
        displayAsPercentage ? (
          <span style={{ fontStyle: 'italic' }}>100%</span>
        ) : (
          <span style={{ fontStyle: 'italic' }}>
            {formatNumber(record.total).toFixed(columnDecimalPlaceValue)}
          </span>
        ),
    },
  ];

  const grandTotals = calculateGrandTotal(data);

  return (
    <Table
      loading={loading}
      className={tableClass}
      columns={columns}
      dataSource={data}
      pagination={false}
      bordered
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}></Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            {' '}
            <strong
              style={{
                fontSize: '16px',
                fontFamily: 'myGramaticaMedium',
                color: '#000',
              }}
            >
              Grand Total
            </strong>
          </Table.Summary.Cell>

          <Table.Summary.Cell index={2}>
            {displayAsPercentage
              ? `${formatNumber((grandTotals.main / grandTotals.total) * 100).toFixed(columnDecimalPlaceValue)}%`
              : formatNumber(grandTotals.main).toFixed(columnDecimalPlaceValue)}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3}>
            {displayAsPercentage
              ? `${formatNumber((grandTotals.drinks / grandTotals.total) * 100).toFixed(columnDecimalPlaceValue)}%`
              : formatNumber(grandTotals.drinks).toFixed(
                  columnDecimalPlaceValue,
                )}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4}>
            {displayAsPercentage
              ? `${formatNumber((grandTotals.deserts / grandTotals.total) * 100).toFixed(columnDecimalPlaceValue)}%`
              : formatNumber(grandTotals.deserts).toFixed(
                  columnDecimalPlaceValue,
                )}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5}>
            {displayAsPercentage
              ? `${formatNumber((grandTotals.others / grandTotals.total) * 100).toFixed(columnDecimalPlaceValue)}%`
              : formatNumber(grandTotals.others)}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6}>
            {displayAsPercentage
              ? '100%'
              : formatNumber(
                  showComboDiscount
                    ? grandTotals.comboDiscount
                    : grandTotals.total,
                ).toFixed(columnDecimalPlaceValue)}
          </Table.Summary.Cell>

          {showComboDiscount && (
            <Table.Summary.Cell index={7}>
              {displayAsPercentage
                ? formatNumber(grandTotals.comboDiscount)
                : formatNumber(grandTotals.total).toFixed(
                    columnDecimalPlaceValue,
                  )}
            </Table.Summary.Cell>
          )}
        </Table.Summary.Row>
      )}
    />
  );
};

// Main Sales Data Table Component
const AnalyticsSalesTable: React.FC<AnalyticsSalesTableProps> = ({
  table1,
  table2,
  table1Loading,
  table2Loading,
}) => {
  const { getLanguage } = useTolgee();

  const language = getLanguage() || 'fr';
  const formattedDataTable1 = useMemo(
    () => formatData(table1, language),
    [table1, language],
  );
  const formattedDataTable2 = useMemo(
    () => formatData(table2, language),
    [table2, language],
  );

  return (
    <div className='sales-data-table container page-break cont'>
      <div>
        {/* Table for Actual Numbers */}
        <div className='table first-table'>
          <SalesTable
            data={formattedDataTable1}
            displayAsPercentage={false}
            tableClass='table-blue'
            loading={table1Loading}
          />
        </div>

        {/* Table for Percentages */}
        <div className='table second-table'>
          <SalesTable
            data={formattedDataTable1}
            displayAsPercentage={true}
            tableClass='table-yellow'
            loading={table1Loading}
          />
        </div>

        {/* Table with Combo Discount */}
        <div className='table third-table'>
          <SalesTable
            data={formattedDataTable2}
            displayAsPercentage={false}
            showComboDiscount={true}
            displayValueInCHF={true}
            tableClass='table-green'
            loading={table2Loading}
            columnDecimalPlaceValue={2}
          />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSalesTable;
